import React, { useState, useEffect, useRef } from "react";
import FormFiller from "./FormFiller";
import PDFFieldMapper from "./PDFFieldMapper";

/*
 * NATIONAL TITLE USA — Redesign with Full Forms Support
 * Content: Adapted from nationaltitleusa.com structure
 * Includes all form categories matching the original website
 */

const tokens = `
  :root {
    /* Professional Legal Color Palette */
    --navy: #1a2744;
    --navy-deep: #0f1929;
    --navy-light: #2a3a5c;
    --ink: #1a1a1a;
    --charcoal: #2d2d2d;
    --muted: #4a5568;
    --subtle: #718096;
    --surface: #f7f8fa;
    --surface-warm: #faf9f7;
    --surface-legal: #f8f9fb;
    --white: #ffffff;
    --gold: #c9a227;
    --gold-rich: #b8860b;
    --gold-light: #e6d48a;
    --gold-muted: rgba(201, 162, 39, 0.12);
    --gold-glow: rgba(201, 162, 39, 0.25);
    --border: rgba(26, 39, 68, 0.12);
    --border-strong: rgba(26, 39, 68, 0.2);
    --shadow-sm: 0 1px 3px rgba(26, 39, 68, 0.08);
    --shadow-md: 0 4px 12px rgba(26, 39, 68, 0.1);
    --shadow-lg: 0 8px 30px rgba(26, 39, 68, 0.12);
    --shadow-gold: 0 4px 20px rgba(201, 162, 39, 0.2);
    --font-serif: "Playfair Display", Georgia, serif;
    --font-sans: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
    --ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
    --nav-height: 4.5rem;
  }
`;

/* --- FORM DATA ---
 * Update this data after running the Python scraper.
 * File paths assume forms are in: /public/forms/{category}/{filename}
 */
const FORMS_DATA = {
  General: [
    { name: "Affidavit of Title", filename: "Affidavit_of_Title.pdf" },
    { name: "Assignment of Mortgage", filename: "Assignment_of_Mortgage.pdf" },
    { name: "Bargain and Sale Deed with Covenant", filename: "Bargain_and_Sale_Deed_with_Covenant.pdf" },
    { name: "Bargain and Sale Deed", filename: "Bargain_and_Sale_Deed.pdf" },
    { name: "Certificate of Reduction of Mortgage", filename: "Certificate_of_Reduction_of_Mortgage.pdf" },
    { name: "CEMA Application", filename: "CEMA_Application.pdf" },
    { name: "Executor's Deed", filename: "Executors_Deed.pdf" },
    { name: "Full Covenant and Warranty Deed", filename: "Full_Covenant_and_Warranty_Deed.pdf" },
    { name: "Power of Attorney Statutory Short Form", filename: "Power_of_Attorney_Statutory_Short_Form.pdf" },
    { name: "Quitclaim Deed", filename: "Quitclaim_Deed.pdf" },
    { name: "Satisfaction of Mortgage", filename: "Satisfaction_of_Mortgage.pdf" },
    { name: "Subordination Agreement", filename: "Subordination_Agreement.pdf" },
  ],
  Multisite: [
    { name: "Multisite Title Application", filename: "Multisite_Title_Application.pdf" },
    { name: "Multisite Search Request Form", filename: "Multisite_Search_Request_Form.pdf" },
    { name: "Bulk Order Form", filename: "Bulk_Order_Form.pdf" },
  ],
  Miscellaneous: [
    { name: "Affidavit for Correction of Deed", filename: "Affidavit_for_Correction_of_Deed.pdf" },
    { name: "Affidavit of Heirship", filename: "Affidavit_of_Heirship.pdf" },
    { name: "Affidavit of Identity", filename: "Affidavit_of_Identity.pdf" },
    { name: "Certificate of Acknowledgment", filename: "Certificate_of_Acknowledgment.pdf" },
    { name: "Corporate Resolution", filename: "Corporate_Resolution.pdf" },
    { name: "Gap Indemnity", filename: "Gap_Indemnity.pdf" },
    { name: "Non-Foreign Affidavit (FIRPTA)", filename: "Non_Foreign_Affidavit_FIRPTA.pdf" },
    { name: "Smoke Detector Affidavit", filename: "Smoke_Detector_Affidavit.pdf" },
  ],
  Mortgages_UCCs: [
    { name: "Assignment of Mortgage", filename: "Assignment_of_Mortgage.pdf" },
    { name: "Certificate of Reduction", filename: "Certificate_of_Reduction.pdf" },
    { name: "Consolidation Agreement", filename: "Consolidation_Agreement.pdf" },
    { name: "Mortgage Extension Agreement", filename: "Mortgage_Extension_Agreement.pdf" },
    { name: "Mortgage Modification Agreement", filename: "Mortgage_Modification_Agreement.pdf" },
    { name: "Satisfaction of Mortgage", filename: "Satisfaction_of_Mortgage.pdf" },
    { name: "UCC-1 Financing Statement", filename: "UCC_1_Financing_Statement.pdf" },
    { name: "UCC-3 Amendment", filename: "UCC_3_Amendment.pdf" },
  ],
  Transfer_Tax: [
    { name: "NYC RPT Form", filename: "NYC_RPT_Form.pdf" },
    { name: "RP-5217 Transfer Report", filename: "RP_5217_Transfer_Report.pdf" },
    { name: "TP-584 Combined Real Estate Transfer Tax", filename: "TP_584.pdf" },
    { name: "TP-584.1 Schedule of Gross Consideration", filename: "TP_584_1.pdf" },
  ],
  Retainer_Deeds: [
    { name: "Attorney Retainer Agreement", filename: "Attorney_Retainer_Agreement.pdf" },
    { name: "Bargain and Sale Deed", filename: "Bargain_and_Sale_Deed.pdf" },
    { name: "Deed with Full Covenants", filename: "Deed_with_Full_Covenants.pdf" },
    { name: "Executor's Deed", filename: "Executors_Deed.pdf" },
    { name: "Quitclaim Deed", filename: "Quitclaim_Deed.pdf" },
  ],
  Contracts_of_Sale: [
    { name: "Contract of Sale - Residential", filename: "Contract_of_Sale_Residential.pdf" },
    { name: "Contract of Sale - Commercial", filename: "Contract_of_Sale_Commercial.pdf" },
    { name: "Contract of Sale - Co-op", filename: "Contract_of_Sale_Coop.pdf" },
    { name: "Contract Rider", filename: "Contract_Rider.pdf" },
    { name: "Lead Paint Disclosure", filename: "Lead_Paint_Disclosure.pdf" },
  ],
};

const FORM_CATEGORIES = {
  General: { title: "General Forms", description: "Essential documents for standard real estate transactions including deeds, mortgages, and power of attorney forms." },
  Multisite: { title: "Multisite Forms", description: "Forms for handling multiple property transactions and bulk title searches." },
  Miscellaneous: { title: "Miscellaneous Forms", description: "Supporting documents including affidavits, certificates, and supplementary forms." },
  Mortgages_UCCs: { title: "Mortgages / UCCs", description: "Mortgage-related documents and Uniform Commercial Code forms for secured transactions." },
  Transfer_Tax: { title: "Transfer Tax and Related Documents", description: "Tax forms required for real estate transfers in New York State and New York City." },
  Retainer_Deeds: { title: "Retainer & Deeds", description: "Legal retainer agreements and various deed types for property transfers." },
  Contracts_of_Sale: { title: "Contracts of Sale", description: "Standard purchase contracts for residential, commercial, and co-op transactions." },
};

/* --- UTILITIES --- */
function Reveal({ children, className = "", delay = 0, threshold = 0.15 }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setIsVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } }, { threshold });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return (
    <div ref={ref} className={className} style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.8s var(--ease-out-expo) ${delay}ms, transform 0.8s var(--ease-out-expo) ${delay}ms`, willChange: "transform, opacity" }}>
      {children}
    </div>
  );
}

function Container({ children, className = "" }) {
  return <div className={className} style={{ width: "100%", maxWidth: "1200px", marginLeft: "auto", marginRight: "auto", paddingLeft: "clamp(1.5rem, 4vw, 2.5rem)", paddingRight: "clamp(1.5rem, 4vw, 2.5rem)" }}>{children}</div>;
}

// eslint-disable-next-line no-unused-vars
function Divider() {
  return (
    <div style={{ padding: "3rem 0" }} aria-hidden="true">
      <Container>
        <div style={{ display: "flex", justifyContent: "space-between", height: "1px", position: "relative" }}>
          {[...Array(5)].map((_, i) => (
            <span key={i} style={{ width: "1px", height: i === 2 ? "60px" : "40px", background: i === 2 ? "linear-gradient(to bottom, transparent, var(--gold-muted) 50%, transparent)" : "linear-gradient(to bottom, transparent, var(--hairline-strong) 50%, transparent)", transform: "translateY(-50%)" }} />
          ))}
        </div>
      </Container>
    </div>
  );
}

/* --- ICONS --- */
function DownloadIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v9M4 7l4 4 4-4M2 14h12" /></svg>;
}

function FileIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14,2 14,8 20,8" /></svg>;
}

function ChevronIcon({ open }) {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}><path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

/* --- LEGAL ICON --- */
function ScalesIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18M5 7l7-4 7 4M5 7l-2 9h4l-2-9M19 7l2 9h-4l2-9M3 16h4M17 16h4"/>
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  );
}

/* --- NAVIGATION --- */
function MinimalNav({ currentPage, setPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formsDropdownOpen, setFormsDropdownOpen] = useState(false);
  const [linksDropdownOpen, setLinksDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) { setFormsDropdownOpen(false); setLinksDropdownOpen(false); }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const formLinks = [
    { label: "Fill Out Forms", id: "form-filler", highlight: true },
    { label: "General", id: "forms-general" },
    { label: "Multisite", id: "forms-multisite" },
    { label: "Miscellaneous", id: "forms-miscellaneous" },
    { label: "Mortgages / UCCs", id: "forms-mortgages-uccs" },
    { label: "Transfer Tax", id: "forms-transfer-tax" },
    { label: "Retainer & Deeds", id: "forms-retainer-deeds" },
    { label: "Contracts of Sale", id: "forms-contracts-of-sale" },
  ];

  const externalLinks = [
    { label: "Rates", id: "rates" },
    { label: "Title Industry", id: "links-title-industry" },
    { label: "NYS County Clerks", id: "links-county-clerks" },
  ];

  const navLinks = [
    { label: "Home", id: "home" },
    { label: "Services", id: "services" },
    { label: "About", id: "about" },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar" style={{ background: 'var(--navy-deep)', padding: '0.5rem 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                (212) 867-2969
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                info@nationaltitleusa.com
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold-light)' }}>
              <ShieldIcon />
              <span>Trusted Since 2003 • Licensed & Insured</span>
            </div>
          </div>
        </Container>
      </div>

      <header style={{ position: "sticky", top: 0, zIndex: 100, height: "var(--nav-height)", background: scrolled ? "rgba(255,255,255,0.98)" : "var(--white)", backdropFilter: scrolled ? "blur(12px)" : "none", WebkitBackdropFilter: scrolled ? "blur(12px)" : "none", borderBottom: `1px solid var(--border)`, boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)', transition: "all 0.3s ease", display: "flex", alignItems: "center" }}>
        <Container>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <a href="#top" onClick={(e) => { e.preventDefault(); setPage("home"); window.scrollTo(0,0); }} className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontFamily: "var(--font-serif)", fontSize: "1.35rem", letterSpacing: "-0.02em", textDecoration: "none", color: "var(--navy)", position: "relative", zIndex: 102 }}>
              <div style={{ color: 'var(--gold)', display: 'flex' }}><ScalesIcon /></div>
              <div>
                <span style={{ fontWeight: 500 }}>National Title</span>{" "}<span style={{ fontWeight: 700, color: 'var(--gold-rich)' }}>USA</span>
              </div>
            </a>

            <nav className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: "1.75rem" }}>
              {navLinks.map((link) => (
                <button key={link.id} onClick={() => { setPage(link.id); window.scrollTo(0,0); }} className={`nav-link ${currentPage === link.id ? "active" : ""}`} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>{link.label}</button>
              ))}

              <div className="dropdown-container" style={{ position: 'relative' }}>
                <button onClick={(e) => { e.stopPropagation(); setFormsDropdownOpen(!formsDropdownOpen); setLinksDropdownOpen(false); }} className={`nav-link ${currentPage.startsWith('forms-') || currentPage === 'form-filler' ? "active" : ""}`} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Forms <ChevronIcon open={formsDropdownOpen} />
                </button>
                {formsDropdownOpen && (
                  <div className="dropdown-menu" style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', background: 'var(--white)', border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-lg)', padding: '0.5rem', minWidth: '240px', marginTop: '0.75rem', zIndex: 200, borderRadius: '4px' }}>
                    <div style={{ position: 'absolute', top: '-6px', left: '50%', width: '12px', height: '12px', background: 'var(--white)', border: '1px solid var(--border-strong)', borderBottom: 'none', borderRight: 'none', transform: 'translateX(-50%) rotate(45deg)' }}></div>
                    {formLinks.map((link) => (
                      <button key={link.id} onClick={() => { setPage(link.id); setFormsDropdownOpen(false); window.scrollTo(0,0); }} className="dropdown-item" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.85rem 1.25rem', background: currentPage === link.id ? 'var(--surface-legal)' : (link.highlight ? 'linear-gradient(135deg, var(--gold-muted), rgba(201, 162, 39, 0.05))' : 'transparent'), border: 'none', cursor: 'pointer', fontSize: '0.875rem', color: link.highlight ? 'var(--gold-rich)' : 'var(--ink)', fontFamily: 'inherit', fontWeight: link.highlight ? '600' : '400', borderBottom: link.highlight ? '1px solid var(--border)' : 'none', marginBottom: link.highlight ? '0.25rem' : '0', transition: 'all 0.2s ease', borderRadius: '3px' }}>{link.highlight && <span style={{ marginRight: '0.5rem' }}>✦</span>}{link.label}</button>
                    ))}
                  </div>
                )}
              </div>

              <div className="dropdown-container" style={{ position: 'relative' }}>
                <button onClick={(e) => { e.stopPropagation(); setLinksDropdownOpen(!linksDropdownOpen); setFormsDropdownOpen(false); }} className={`nav-link ${currentPage.startsWith('links-') || currentPage === 'rates' ? "active" : ""}`} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Resources <ChevronIcon open={linksDropdownOpen} />
                </button>
                {linksDropdownOpen && (
                  <div className="dropdown-menu" style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', background: 'var(--white)', border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-lg)', padding: '0.5rem', minWidth: '200px', marginTop: '0.75rem', zIndex: 200, borderRadius: '4px' }}>
                    <div style={{ position: 'absolute', top: '-6px', left: '50%', width: '12px', height: '12px', background: 'var(--white)', border: '1px solid var(--border-strong)', borderBottom: 'none', borderRight: 'none', transform: 'translateX(-50%) rotate(45deg)' }}></div>
                    {externalLinks.map((link) => (
                      <button key={link.id} onClick={() => { setPage(link.id); setLinksDropdownOpen(false); window.scrollTo(0,0); }} className="dropdown-item" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.85rem 1.25rem', background: currentPage === link.id ? 'var(--surface-legal)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--ink)', fontFamily: 'inherit', transition: 'all 0.2s ease', borderRadius: '3px' }}>{link.label}</button>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={() => { setPage("contact"); window.scrollTo(0,0); }} className={`nav-link ${currentPage === "contact" ? "active" : ""}`} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Contact</button>
              <button onClick={() => { setPage("order-title"); window.scrollTo(0,0); }} className="btn btn--primary btn--sm">Order Title</button>
            </nav>

            <button className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ zIndex: 102, background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}>
              <div style={{ width: '24px', height: '2px', background: 'var(--navy)', marginBottom: '6px', transition: '0.3s', transform: mobileMenuOpen ? 'rotate(45deg) translate(5px, 6px)' : 'none' }}></div>
              <div style={{ width: '24px', height: '2px', background: 'var(--navy)', transition: '0.3s', opacity: mobileMenuOpen ? 0 : 1 }}></div>
              <div style={{ width: '24px', height: '2px', background: 'var(--navy)', marginTop: '6px', transition: '0.3s', transform: mobileMenuOpen ? 'rotate(-45deg) translate(5px, -6px)' : 'none' }}></div>
            </button>
          </div>
        </Container>
      </header>

      <MobileMenu isOpen={mobileMenuOpen} setPage={setPage} setMobileMenuOpen={setMobileMenuOpen} formLinks={formLinks} externalLinks={externalLinks} navLinks={navLinks} />
    </>
  );
}

function MobileMenu({ isOpen, setPage, setMobileMenuOpen, formLinks, externalLinks, navLinks }) {
  const [formsExpanded, setFormsExpanded] = useState(false);
  const [linksExpanded, setLinksExpanded] = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--white)', zIndex: 101, display: 'flex', flexDirection: 'column', paddingTop: 'calc(var(--nav-height) + 1rem)', overflowY: 'auto', transform: isOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.4s var(--ease-out-expo)' }}>
      <div style={{ padding: '1.5rem 2rem' }}>
        {/* Logo Area */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '2px solid var(--gold-muted)' }}>
          <div style={{ color: 'var(--gold)' }}><ScalesIcon /></div>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.25rem" }}>
            <span style={{ fontWeight: 500, color: 'var(--navy)' }}>National Title</span>{" "}<span style={{ fontWeight: 700, color: 'var(--gold-rich)' }}>USA</span>
          </div>
        </div>

        {navLinks.map((link) => (
          <button key={link.id} onClick={() => { setPage(link.id); setMobileMenuOpen(false); window.scrollTo(0,0); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '1.125rem', fontFamily: 'var(--font-serif)', color: 'var(--navy)', cursor: 'pointer', padding: '1rem 0', borderBottom: '1px solid var(--border)' }}>{link.label}</button>
        ))}

        <button onClick={() => setFormsExpanded(!formsExpanded)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '1.125rem', fontFamily: 'var(--font-serif)', color: 'var(--navy)', cursor: 'pointer', padding: '1rem 0', borderBottom: '1px solid var(--border)' }}>
          Forms <ChevronIcon open={formsExpanded} />
        </button>
        {formsExpanded && (
          <div style={{ paddingLeft: '1rem', background: 'var(--surface-legal)', margin: '0 -2rem', padding: '0.5rem 2rem 0.5rem 3rem' }}>
            {formLinks.map((link) => (
              <button key={link.id} onClick={() => { setPage(link.id); setMobileMenuOpen(false); window.scrollTo(0,0); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '0.9375rem', fontFamily: 'var(--font-sans)', color: link.highlight ? 'var(--gold-rich)' : 'var(--muted)', fontWeight: link.highlight ? '600' : '400', cursor: 'pointer', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>{link.highlight && '✦ '}{link.label}</button>
            ))}
          </div>
        )}

        <button onClick={() => setLinksExpanded(!linksExpanded)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '1.125rem', fontFamily: 'var(--font-serif)', color: 'var(--navy)', cursor: 'pointer', padding: '1rem 0', borderBottom: '1px solid var(--border)' }}>
          Resources <ChevronIcon open={linksExpanded} />
        </button>
        {linksExpanded && (
          <div style={{ paddingLeft: '1rem', background: 'var(--surface-legal)', margin: '0 -2rem', padding: '0.5rem 2rem 0.5rem 3rem' }}>
            {externalLinks.map((link) => (
              <button key={link.id} onClick={() => { setPage(link.id); setMobileMenuOpen(false); window.scrollTo(0,0); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '0.9375rem', fontFamily: 'var(--font-sans)', color: 'var(--muted)', cursor: 'pointer', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>{link.label}</button>
            ))}
          </div>
        )}

        <button onClick={() => { setPage("contact"); setMobileMenuOpen(false); window.scrollTo(0,0); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', fontSize: '1.125rem', fontFamily: 'var(--font-serif)', color: 'var(--navy)', cursor: 'pointer', padding: '1rem 0', borderBottom: '1px solid var(--border)' }}>Contact</button>
        <button onClick={() => { setPage("order-title"); setMobileMenuOpen(false); window.scrollTo(0,0); }} className="btn btn--gold" style={{ width: '100%', marginTop: '2rem' }}>Order Title</button>
      </div>
    </div>
  );
}

/* --- PAGES --- */
function Home({ setPage }) {
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const handleScroll = () => setParallaxOffset(Math.min(window.scrollY * 0.025, 15));
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const trustBadges = [
    { icon: "🏛️", label: "20+ Years", desc: "of Excellence" },
    { icon: "📋", label: "10,000+", desc: "Transactions" },
    { icon: "⚖️", label: "Licensed", desc: "& Insured" },
  ];

  return (
    <>
      {/* Hero Section */}
      <section id="top" style={{ position: 'relative', background: 'linear-gradient(135deg, var(--navy-deep) 0%, var(--navy) 50%, var(--navy-light) 100%)', overflow: 'hidden' }}>
        {/* Decorative Pattern */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>

        <Container>
          <div style={{ padding: "clamp(4rem, 8vw, 7rem) 0", display: "grid", gap: "clamp(3rem, 6vw, 5rem)", alignItems: "center", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 480px), 1fr))" }}>
            <div style={{ maxWidth: "600px" }}>
              <Reveal>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(201, 162, 39, 0.15)', border: '1px solid rgba(201, 162, 39, 0.3)', padding: '0.5rem 1rem', marginBottom: '1.5rem', fontSize: '0.8125rem', color: 'var(--gold-light)', fontWeight: '500', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  <span style={{ width: '8px', height: '8px', background: 'var(--gold)', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
                  New York's Premier Title Agency
                </div>
              </Reveal>
              <Reveal delay={50}>
                <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.75rem, 6vw, 4.5rem)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "var(--white)", marginBottom: "1.75rem", fontWeight: 500 }}>
                  Trusted Expertise,{" "}<span style={{ color: 'var(--gold)', position: 'relative', display: 'inline-block' }}>Unmatched<span style={{ position: 'absolute', bottom: '-4px', left: 0, width: '100%', height: '3px', background: 'var(--gold)', animation: 'heroUnderline 1.2s var(--ease-out-expo) 0.8s both' }}></span></span>{" "}Service
                </h1>
              </Reveal>
              <Reveal delay={100}>
                <p style={{ fontSize: "1.125rem", lineHeight: 1.8, color: "rgba(255,255,255,0.8)", marginBottom: "1.25rem" }}>
                  With over 20 years of experience, we are proud to be one of New York's largest and most trusted title companies. We combine a tradition of excellence with a commitment to innovation.
                </p>
              </Reveal>
              <Reveal delay={150}>
                <p style={{ fontSize: "1.125rem", lineHeight: 1.8, color: "rgba(255,255,255,0.7)", marginBottom: "2.5rem" }}>
                  Our expert team handles every search and report with speed, precision, and care—ensuring timely recordings and swift disbursement.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button onClick={() => { setPage("order-title"); window.scrollTo(0,0); }} className="btn btn--gold">
                    Order Title Now
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <button onClick={() => setPage("services")} className="btn btn--outline-light">View Services</button>
                </div>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <figure className="hero-frame" style={{ position: "relative", overflow: "hidden", margin: 0, boxShadow: 'var(--shadow-lg), 0 0 60px rgba(201, 162, 39, 0.15)' }}>
                <span className="hero-corner hero-corner--tl" /><span className="hero-corner hero-corner--br" />
                <div style={{ transform: `translateY(${parallaxOffset}px)`, willChange: "transform", transition: "transform 0.1s ease-out" }}>
                  <img src="https://images.unsplash.com/photo-1534430480872-3498386e7856?w=1200&q=80&auto=format" alt="New York City skyline" style={{ width: "100%", height: "clamp(360px, 50vw, 500px)", objectFit: "cover", transform: "scale(1.05)", transition: "transform 6s cubic-bezier(0.25, 1, 0.5, 1)" }} className="hero-image" />
                </div>
              </figure>
            </Reveal>
          </div>
        </Container>

        {/* Bottom Decorative Line */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, transparent, var(--gold) 20%, var(--gold) 80%, transparent)' }}></div>
      </section>

      {/* Trust Badges */}
      <section style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0' }}>
        <Container>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', borderLeft: '1px solid var(--border)' }}>
            {trustBadges.map((badge, i) => (
              <Reveal key={i} delay={i * 100}>
                <div style={{ padding: '2rem 1.5rem', textAlign: 'center', borderRight: '1px solid var(--border)', background: i === 1 ? 'var(--surface-legal)' : 'transparent' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{badge.icon}</div>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.5rem', fontWeight: '600', color: 'var(--navy)', lineHeight: 1.2 }}>{badge.label}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{badge.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Underwriters Section */}
      <section style={{ padding: '3rem 0', background: 'var(--surface-warm)' }}>
        <Container>
          <div style={{ textAlign: 'center' }}>
            <Reveal>
              <p style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--subtle)', marginBottom: '1.5rem', fontWeight: '500' }}>Proudly Underwritten By</p>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3rem', flexWrap: 'wrap', opacity: 0.7 }}>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.125rem', fontWeight: '600', color: 'var(--navy)' }}>Old Republic</div>
                <div style={{ width: '1px', height: '24px', background: 'var(--border-strong)' }}></div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.125rem', fontWeight: '600', color: 'var(--navy)' }}>First American</div>
                <div style={{ width: '1px', height: '24px', background: 'var(--border-strong)' }}></div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1.125rem', fontWeight: '600', color: 'var(--navy)' }}>Security Title</div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <OrderSection setPage={setPage} />
    </>
  );
}

function About() {
  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'var(--white)' }}>
      <Container>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "4rem", alignItems: 'start' }}>
          {/* Left Column */}
          <div>
            <Reveal>
              <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>About Us</div>
              <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.5rem, 5vw, 3.5rem)", marginBottom: "2rem", color: 'var(--navy)', lineHeight: 1.1 }}>A Legacy of Trust & Excellence</h1>
            </Reveal>
            <Reveal delay={100}>
              <div style={{ padding: '2rem', background: 'var(--surface-legal)', border: '1px solid var(--border)', borderLeft: '4px solid var(--gold)', marginBottom: '2rem' }}>
                <p style={{ color: "var(--navy)", lineHeight: 1.8, fontSize: '1.0625rem', fontStyle: 'italic', margin: 0 }}>
                  "Established over two decades ago, National Title USA has grown from a boutique agency into one of the premier title insurance providers in New York."
                </p>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginBottom: "1.5rem", fontSize: '1.0625rem' }}>
                Our journey is built on a foundation of integrity, accuracy, and unwavering client support. We proudly underwrite through <strong style={{ color: 'var(--navy)' }}>Old Republic</strong>, <strong style={{ color: 'var(--navy)' }}>First American Title Insurance Company</strong>, and <strong style={{ color: 'var(--navy)' }}>Security Title</strong>.
              </p>
            </Reveal>
          </div>

          {/* Right Column */}
          <div>
            <Reveal delay={200}>
              <div style={{ background: 'linear-gradient(135deg, var(--navy-deep), var(--navy))', padding: '2.5rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '1rem', right: '1rem', width: '60px', height: '60px', border: '2px solid var(--gold)', opacity: 0.3 }}></div>
                <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "1.5rem", marginBottom: "1.25rem", color: "var(--white)", fontWeight: '500' }}>Our Team</h3>
                <p style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.8, marginBottom: "1rem" }}>Our strength lies in our people—seasoned professionals including expert readers, clearance officers, closers, and post-closing specialists.</p>
                <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.8, margin: 0 }}>We understand that in real estate, time is money. Our staff is dedicated to ensuring timely recordings and swift disbursement.</p>
              </div>
            </Reveal>
            <Reveal delay={250}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border)', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', fontWeight: '700', color: 'var(--gold-rich)' }}>20+</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginTop: '0.25rem' }}>Years Experience</div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border)', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', fontWeight: '700', color: 'var(--gold-rich)' }}>3</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginTop: '0.25rem' }}>Major Underwriters</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </div>
  );
}

function Services() {
  const services = [
    { title: "Title Insurance", desc: "Comprehensive title insurance policies protecting owners and lenders against losses due to defects in title.", icon: "🛡️" },
    { title: "Settlement Services", desc: "Full-service settlement handling, ensuring a smooth closing process for residential and commercial transactions.", icon: "📝" },
    { title: "Lien Searches", desc: "Detailed searches for municipal liens, judgments, and other encumbrances that could affect property ownership.", icon: "🔍" },
    { title: "1031 Exchanges", desc: "Expert facilitation of 1031 tax-deferred exchanges to help investors maximize their real estate portfolio growth.", icon: "🔄" },
    { title: "Co-op Services", desc: "Specialized searches and insurance products tailored specifically for cooperative apartment transactions.", icon: "🏢" },
    { title: "Foreclosure Searches", desc: "Accurate and timely foreclosure certificates and searches assisting legal professionals and lenders.", icon: "📋" }
  ];

  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: "linear-gradient(180deg, var(--surface-legal) 0%, var(--white) 100%)" }}>
      <Container>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: "800px", marginLeft: 'auto', marginRight: 'auto', marginBottom: "4rem" }}>
            <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>What We Offer</div>
            <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.5rem, 5vw, 3.5rem)", marginBottom: "1.5rem", color: 'var(--navy)' }}>Our Services</h1>
            <p style={{ fontSize: "1.125rem", color: "var(--muted)", lineHeight: 1.75 }}>We offer a full suite of title and settlement services designed to meet the needs of attorneys, lenders, realtors, and homeowners throughout New York.</p>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.5rem" }}>
          {services.map((s, i) => (
            <Reveal key={i} delay={i * 50}>
              <div className="service-card" style={{ background: "var(--white)", padding: "2rem", border: "1px solid var(--border)", height: "100%", transition: "all 0.3s ease", position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'var(--gold)', transform: 'scaleY(0)', transformOrigin: 'bottom', transition: 'transform 0.3s ease' }} className="service-accent"></div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.25rem' }}>
                  <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>{s.icon}</div>
                  <div>
                    <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "1.25rem", marginBottom: "0.75rem", color: "var(--navy)", fontWeight: '600' }}>{s.title}</h3>
                    <p style={{ color: "var(--muted)", fontSize: "0.9375rem", lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </div>
  );
}

/* --- FORMS PAGE --- */
function FormsPage({ category }) {
  const categoryKey = { "forms-general": "General", "forms-multisite": "Multisite", "forms-miscellaneous": "Miscellaneous", "forms-mortgages-uccs": "Mortgages_UCCs", "forms-transfer-tax": "Transfer_Tax", "forms-retainer-deeds": "Retainer_Deeds", "forms-contracts-of-sale": "Contracts_of_Sale" }[category];
  const forms = FORMS_DATA[categoryKey] || [];
  const categoryInfo = FORM_CATEGORIES[categoryKey] || { title: "Forms", description: "" };
  const formsBasePath = `/forms/${categoryKey}/`;

  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", minHeight: "60vh", background: 'var(--surface-legal)' }}>
      <Container>
        <Reveal>
          <div style={{ marginBottom: "3rem" }}>
            <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Legal Documents</div>
            <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem, 4vw, 3rem)", marginBottom: "1rem", color: 'var(--navy)' }}>{categoryInfo.title}</h1>
            <p style={{ fontSize: "1.0625rem", color: "var(--muted)", lineHeight: 1.75, maxWidth: "700px" }}>{categoryInfo.description}</p>
          </div>
        </Reveal>
        <div style={{ display: "grid", gap: "0.75rem", maxWidth: "900px" }}>
          {forms.map((form, index) => (
            <Reveal key={index} delay={index * 30}>
              <a href={formsBasePath + form.filename} download className="form-item" style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1.25rem 1.5rem", background: "var(--white)", border: "1px solid var(--border)", textDecoration: "none", color: "var(--navy)", transition: "all 0.2s ease", boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', flexShrink: 0 }}><FileIcon /></span>
                <span style={{ flex: 1, fontSize: "0.9375rem", fontWeight: '500' }}>{form.name}</span>
                <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem", color: "var(--white)", padding: "0.5rem 1rem", background: "var(--navy)", fontWeight: '500' }}><DownloadIcon />Download</span>
              </a>
            </Reveal>
          ))}
        </div>
        {forms.length === 0 && (
          <Reveal>
            <div style={{ padding: "4rem 2rem", textAlign: "center", background: "var(--white)", border: "1px solid var(--border)" }}>
              <p style={{ color: "var(--muted)", marginBottom: "1rem" }}>No forms available in this category yet.</p>
              <p style={{ color: "var(--subtle)", fontSize: "0.875rem" }}>Please run the Python scraper to download forms from the website.</p>
            </div>
          </Reveal>
        )}
      </Container>
    </div>
  );
}

/* --- LINKS PAGES --- */
function RatesPage() {
  const rateLinks = [
    { name: "Old Republic Rate Calculator", url: "https://www.oldrepublictitle.com/rate-calculator", desc: "Calculate title insurance premiums with Old Republic" },
    { name: "First American Title Calculator", url: "https://facc.firstam.com/?SSID=0.03371978652279539", desc: "First American's comprehensive rate calculator" }
  ];

  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'var(--surface-legal)' }}>
      <Container>
        <Reveal>
          <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Resources</div>
          <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem, 4vw, 3rem)", marginBottom: "1rem", color: 'var(--navy)' }}>Title Insurance Rates</h1>
          <p style={{ fontSize: "1.0625rem", color: "var(--muted)", lineHeight: 1.75, maxWidth: "700px", marginBottom: "3rem" }}>Title insurance rates in New York are regulated by the New York State Department of Financial Services. Use the calculators below to estimate your costs.</p>
        </Reveal>
        <Reveal delay={100}>
          <div style={{ background: "var(--white)", padding: "2.5rem", border: "1px solid var(--border)", maxWidth: '700px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>
              </div>
              <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "1.375rem", color: 'var(--navy)', margin: 0 }}>Rate Calculators</h3>
            </div>
            <div style={{ display: "grid", gap: "1rem" }}>
              {rateLinks.map((item, i) => (
                <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="form-item" style={{ display: 'flex', alignItems: "center", gap: "1rem", padding: '1.25rem', background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none', color: 'var(--navy)' }}>
                  <div style={{ width: '10px', height: '10px', background: 'var(--gold)', borderRadius: '50%', flexShrink: 0 }}></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{item.name}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>{item.desc}</div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M5 11L11 5M11 5H6M11 5V10" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </Container>
    </div>
  );
}

function TitleIndustryLinksPage() {
  const linkGroups = [
    { title: "Industry Associations", links: [
      { name: "American Land Title Association (ALTA)", url: "https://www.alta.org" },
      { name: "New York State Land Title Association", url: "https://www.nyslta.org" },
      { name: "TIRSA (Title Insurance Rate Service)", url: "https://www.tirsa.org" },
      { name: "Home Closing 101", url: "https://www.homeclosing101.org" },
    ]},
    { title: "Government Resources", links: [
      { name: "Federal Financial Institutions Examination Council", url: "https://www.ffiec.gov" },
      { name: "IRS", url: "https://www.irs.gov" },
      { name: "FDIC", url: "https://www.fdic.gov" },
    ]},
    { title: "NYC Resources", links: [
      { name: "NYC ACRIS", url: "https://www.nyc.gov/site/finance/property/acris.page" },
      { name: "NYC Department of Finance", url: "https://www.nyc.gov/site/finance/index.page" },
      { name: "NYC Clerk's Office", url: "https://www.cityclerk.nyc.gov/content/" },
    ]},
    { title: "County Resources", links: [
      { name: "Dutchess County Government", url: "https://www.dutchessny.gov" },
      { name: "Nassau County Government", url: "https://www.nassaucountyny.gov" },
      { name: "Westchester County Government", url: "https://www.westchestergov.com" },
    ]}
  ];

  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'var(--surface-legal)' }}>
      <Container>
        <Reveal>
          <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Resources</div>
          <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem, 4vw, 3rem)", marginBottom: "1rem", color: 'var(--navy)' }}>Title Industry Links</h1>
          <p style={{ fontSize: "1.0625rem", color: "var(--muted)", lineHeight: 1.75, maxWidth: "700px", marginBottom: "3rem" }}>Essential resources for title professionals, including industry associations, government agencies, and county resources.</p>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "2rem" }}>
          {linkGroups.map((group, gi) => (
            <Reveal key={gi} delay={gi * 100}>
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--navy-deep), var(--navy))', padding: '1rem 1.5rem' }}>
                  <h3 style={{ color: 'var(--white)', fontSize: '1rem', fontWeight: '600', margin: 0 }}>{group.title}</h3>
                </div>
                <div style={{ padding: '0.5rem' }}>
                  {group.links.map((link, index) => (
                    <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", textDecoration: "none", color: "var(--navy)", borderBottom: index < group.links.length - 1 ? '1px solid var(--border)' : 'none', fontSize: '0.9375rem' }} className="form-item">
                      <span>{link.name}</span>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M5 11L11 5M11 5H6M11 5V10" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </div>
  );
}

function CountyClerksPage() {
  const counties = [
    { name: "New York County (Manhattan)", url: "https://ww2.nycourts.gov/courts/1jd/supctmanh/county_clerk_operations.shtml", phone: "212-361-7550", region: "NYC" },
    { name: "Kings County (Brooklyn)", url: "https://ww2.nycourts.gov/COURTS/12JD/BRONX/countyclerk/index.shtml", phone: "718-802-3730", region: "NYC" },
    { name: "Queens County", url: "https://ww2.nycourts.gov/COURTS/11jd/queensclerk/index.shtml", phone: "718-298-7200", region: "NYC" },
    { name: "Bronx County", url: "https://ww2.nycourts.gov/COURTS/12JD/BRONX/countyclerk/index.shtml", phone: "718-579-6820", region: "NYC" },
    { name: "Richmond County (Staten Island)", url: "https://richmondcountyclerk.com", phone: "718-390-5387", region: "NYC" },
    { name: "Nassau County", url: "https://www.nassaucountyny.gov/458/County-Clerk", phone: "516-571-1444", region: "Long Island" },
    { name: "Suffolk County", url: "https://www.suffolkcountyny.gov", phone: "631-852-2043", region: "Long Island" },
    { name: "Westchester County", url: "https://www.westchesterclerk.com/", phone: "914-995-3095", region: "Suburban" },
  ];

  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'var(--surface-legal)' }}>
      <Container>
        <Reveal>
          <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Resources</div>
          <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem, 4vw, 3rem)", marginBottom: "1rem", color: 'var(--navy)' }}>NYS County Clerks</h1>
          <p style={{ fontSize: "1.0625rem", color: "var(--muted)", lineHeight: 1.75, maxWidth: "700px", marginBottom: "3rem" }}>Contact information for county clerk offices throughout the New York metropolitan area.</p>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem", maxWidth: "900px" }}>
          {counties.map((county, index) => (
            <Reveal key={index} delay={index * 50}>
              <a href={county.url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1.25rem 1.5rem", background: "var(--white)", border: "1px solid var(--border)", textDecoration: "none", color: "var(--navy)", boxShadow: 'var(--shadow-sm)' }} className="form-item">
                <div style={{ width: '48px', height: '48px', background: county.region === 'NYC' ? 'linear-gradient(135deg, var(--navy-deep), var(--navy))' : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={county.region === 'NYC' ? 'var(--gold-light)' : 'var(--navy)'} strokeWidth="1.5"><path d="M3 21h18M5 21V7l8-4 8 4v14M9 21v-6h6v6"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: "600", marginBottom: '0.25rem' }}>{county.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: "0.875rem", color: "var(--muted)" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    {county.phone}
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M5 11L11 5M11 5H6M11 5V10" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            </Reveal>
          ))}
        </div>
      </Container>
    </div>
  );
}

/* --- CONTACT & ORDER --- */
function Contact() {
  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'var(--surface-legal)' }}>
      <Container>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Get In Touch</div>
            <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.5rem, 5vw, 3.5rem)", color: 'var(--navy)', marginBottom: "1rem" }}>Contact Us</h1>
            <p style={{ fontSize: "1.125rem", color: "var(--muted)", lineHeight: 1.75, maxWidth: '600px', margin: '0 auto' }}>Our team of experienced professionals is ready to assist you with all your title needs.</p>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "2rem" }}>
          <Reveal delay={100}>
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', height: '100%' }}>
              <div style={{ background: 'linear-gradient(135deg, var(--navy-deep), var(--navy))', padding: '1.5rem 2rem' }}>
                <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--gold-light)", marginBottom: "0.5rem" }}>New York City Office</h4>
                <p style={{ fontSize: "1.125rem", color: "var(--white)", fontWeight: "600", margin: 0 }}>Headquarters</p>
              </div>
              <div style={{ padding: '2rem' }}>
                <p style={{ fontSize: "1rem", color: "var(--navy)", lineHeight: 1.6, fontWeight: "600", marginBottom: '0.5rem' }}>Balram Kakkar, Esq.</p>
                <p style={{ fontSize: "0.875rem", color: "var(--gold-rich)", marginBottom: '1.5rem' }}>Chief Executive Officer</p>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <span style={{ color: 'var(--muted)', fontSize: '0.9375rem', lineHeight: 1.5 }}>116 E. 30th Street<br/>New York, NY 10016</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    <span style={{ color: 'var(--muted)', fontSize: '0.9375rem' }}>(212) 867-2969 x 203</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
                    <span style={{ color: 'var(--muted)', fontSize: '0.9375rem' }}>(212) 867-3065 fax</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', height: '100%' }}>
              <div style={{ background: 'var(--surface)', padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--subtle)", marginBottom: "0.5rem" }}>Long Island Office</h4>
                <p style={{ fontSize: "1.125rem", color: "var(--navy)", fontWeight: "600", margin: 0 }}>Regional Branch</p>
              </div>
              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <span style={{ color: 'var(--muted)', fontSize: '0.9375rem', lineHeight: 1.5 }}>21B Jerusalem Avenue<br/>Hicksville, NY 11801</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    <span style={{ color: 'var(--muted)', fontSize: '0.9375rem' }}>(516) 307-3635</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <form style={{ background: "var(--white)", padding: "2rem", border: "1px solid var(--border)", height: '100%' }} onSubmit={(e) => e.preventDefault()}>
              <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "1.375rem", marginBottom: "1.5rem", color: 'var(--navy)' }}>Send a Message</h3>
              <div style={{ display: "grid", gap: "1.25rem" }}>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Name</label><input type="text" className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem', transition: 'all 0.2s ease' }} /></div>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Email</label><input type="email" className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem', transition: 'all 0.2s ease' }} /></div>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Message</label><textarea rows="3" className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", resize: "vertical", fontSize: '0.9375rem', transition: 'all 0.2s ease' }}></textarea></div>
                <button className="btn btn--primary" style={{ width: "100%" }}>Send Message</button>
              </div>
            </form>
          </Reveal>
        </div>
      </Container>
    </div>
  );
}

function OrderTitle() {
  return (
    <div style={{ padding: "clamp(5rem, 8vw, 8rem) 0", background: 'linear-gradient(180deg, var(--surface-legal) 0%, var(--white) 100%)' }}>
      <Container>
        <Reveal>
          <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center", marginBottom: "3rem" }}>
            <div style={{ display: 'inline-block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-rich)', marginBottom: '1rem', fontWeight: '600', background: 'var(--gold-muted)', padding: '0.5rem 1rem' }}>Start Your Order</div>
            <h1 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2.5rem, 5vw, 3.5rem)", marginBottom: "1rem", color: 'var(--navy)' }}>Order Title</h1>
            <p style={{ fontSize: "1.125rem", color: "var(--muted)", lineHeight: 1.75 }}>Fill out the form below to submit your title order. Our team will contact you within 24 hours.</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <form style={{ maxWidth: "750px", margin: "0 auto", background: "var(--white)", padding: "clamp(2rem, 5vw, 3rem)", border: "1px solid var(--border)", boxShadow: 'var(--shadow-lg)' }} onSubmit={(e) => e.preventDefault()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg>
              </div>
              <div>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', color: 'var(--navy)', margin: 0 }}>Title Order Request</h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--muted)', margin: 0 }}>All fields marked with * are required</p>
              </div>
            </div>
            <div style={{ display: "grid", gap: "1.5rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>First Name <span style={{ color: 'var(--gold-rich)' }}>*</span></label><input type="text" required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Last Name <span style={{ color: 'var(--gold-rich)' }}>*</span></label><input type="text" required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
              </div>
              <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Firm / Company Name</label><input type="text" className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Email <span style={{ color: 'var(--gold-rich)' }}>*</span></label><input type="email" required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Phone <span style={{ color: 'var(--gold-rich)' }}>*</span></label><input type="tel" required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
              </div>
              <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Property Address <span style={{ color: 'var(--gold-rich)' }}>*</span></label><input type="text" required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>County <span style={{ color: 'var(--gold-rich)' }}>*</span></label><select required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }}><option value="">Select County</option><option>New York (Manhattan)</option><option>Kings (Brooklyn)</option><option>Queens</option><option>Bronx</option><option>Richmond (Staten Island)</option><option>Nassau</option><option>Suffolk</option><option>Westchester</option></select></div>
                <div><label style={{ display: "block", fontSize: "0.8125rem", marginBottom: "0.5rem", color: "var(--muted)", fontWeight: '500' }}>Transaction Type <span style={{ color: 'var(--gold-rich)' }}>*</span></label><select required className="form-input" style={{ width: "100%", padding: "0.875rem 1rem", border: "1px solid var(--border-strong)", background: "var(--surface)", fontFamily: "inherit", fontSize: '0.9375rem' }}><option value="">Select Type</option><option>Purchase</option><option>Refinance</option><option>CEMA</option><option>Title Search Only</option></select></div>
              </div>
              <button className="btn btn--gold" style={{ width: "100%", marginTop: "0.5rem", padding: '1rem 2rem', fontSize: '1rem' }}>
                Submit Order
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </form>
        </Reveal>
      </Container>
    </div>
  );
}

function OrderSection({ setPage }) {
  return (
    <section style={{ padding: "clamp(5rem, 10vw, 8rem) 0", background: "linear-gradient(135deg, var(--navy-deep) 0%, var(--navy) 100%)", position: 'relative', overflow: 'hidden' }}>
      {/* Decorative Elements */}
      <div style={{ position: 'absolute', top: '20%', left: '5%', width: '200px', height: '200px', border: '1px solid rgba(201, 162, 39, 0.1)', transform: 'rotate(45deg)' }}></div>
      <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: '150px', height: '150px', border: '1px solid rgba(201, 162, 39, 0.1)', transform: 'rotate(15deg)' }}></div>

      <Container>
        <Reveal>
          <div style={{ position: "relative", textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <div style={{ width: '40px', height: '1px', background: 'var(--gold)' }}></div>
              <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--gold-light)', fontWeight: '500' }}>Ready to Begin?</span>
              <div style={{ width: '40px', height: '1px', background: 'var(--gold)' }}></div>
            </div>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 500, color: "var(--white)", marginBottom: "1.5rem", lineHeight: 1.15 }}>Order Your Title Today</h2>
            <p style={{ fontSize: "1.125rem", lineHeight: 1.75, color: "rgba(255,255,255,0.75)", marginBottom: "2.5rem", maxWidth: '550px', marginLeft: 'auto', marginRight: 'auto' }}>Experience the National Title USA difference. Fast, reliable, and professional title services backed by 20+ years of expertise.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { setPage("order-title"); window.scrollTo(0, 0); }} className="btn btn--gold">
                Get Started
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <button onClick={() => { setPage("contact"); window.scrollTo(0, 0); }} className="btn btn--outline-light">Contact Us</button>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function Footer({ setPage }) {
  return (
    <footer style={{ background: "var(--navy-deep)", color: 'var(--white)', position: "relative" }}>
      {/* Main Footer */}
      <div style={{ padding: "4rem 0 3rem", borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Container>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "3rem" }}>
            {/* Brand Column */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{ color: 'var(--gold)' }}><ScalesIcon /></div>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.25rem" }}>
                  <span style={{ fontWeight: 500 }}>National Title</span>{" "}<span style={{ fontWeight: 700, color: 'var(--gold)' }}>USA</span>
                </div>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9375rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                New York's premier title insurance agency. Trusted by attorneys, lenders, and homeowners for over 20 years.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {['Old Republic', 'First American', 'Security Title'].map((name, i) => (
                  <div key={i} style={{ padding: '0.375rem 0.625rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.6875rem', color: 'rgba(255,255,255,0.5)' }}>{name}</div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold-light)', marginBottom: '1.25rem', fontWeight: '600' }}>Quick Links</h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {[{label: "Home", id: "home"}, {label: "Services", id: "services"}, {label: "About Us", id: "about"}, {label: "Order Title", id: "order-title"}, {label: "Contact", id: "contact"}].map(item => (
                  <button key={item.id} onClick={() => { setPage(item.id); window.scrollTo(0,0); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9375rem', color: 'rgba(255,255,255,0.6)', textAlign: 'left', padding: 0, transition: 'color 0.2s ease' }} className="footer-link">{item.label}</button>
                ))}
              </div>
            </div>

            {/* Forms */}
            <div>
              <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold-light)', marginBottom: '1.25rem', fontWeight: '600' }}>Forms & Resources</h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {[{label: "Fill Out Forms", id: "form-filler"}, {label: "General Forms", id: "forms-general"}, {label: "Transfer Tax Forms", id: "forms-transfer-tax"}, {label: "Rate Calculators", id: "rates"}, {label: "County Clerks", id: "links-county-clerks"}].map(item => (
                  <button key={item.id} onClick={() => { setPage(item.id); window.scrollTo(0,0); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9375rem', color: 'rgba(255,255,255,0.6)', textAlign: 'left', padding: 0, transition: 'color 0.2s ease' }} className="footer-link">{item.label}</button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold-light)', marginBottom: '1.25rem', fontWeight: '600' }}>Contact Us</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" style={{ marginTop: '2px', flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', lineHeight: 1.5 }}>116 E. 30th Street<br/>New York, NY 10016</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>(212) 867-2969</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>info@nationaltitleusa.com</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Bottom Bar */}
      <div style={{ padding: '1.5rem 0' }}>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.4)" }}>
              © {new Date().getFullYear()} National Title USA Agency, Inc. All rights reserved.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
              <ShieldIcon />
              <span>Licensed & Regulated by NYS DFS</span>
            </div>
          </div>
        </Container>
      </div>

      {/* Gold Accent Line */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, var(--gold), var(--gold-light), var(--gold))" }} />
    </footer>
  );
}

export default function App() {
  const [page, setPage] = useState("home");

  // Hidden route access via URL hash or keyboard shortcut
  useEffect(() => {
    // Check URL hash for hidden pages
    const checkHash = () => {
      if (window.location.hash === '#pdf-mapper') {
        setPage('pdf-mapper');
      }
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);

    // Keyboard shortcut: Ctrl+Shift+M to access PDF Mapper
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        setPage('pdf-mapper');
        window.location.hash = 'pdf-mapper';
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('hashchange', checkHash);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const renderPage = () => {
    switch (page) {
      case "home": return <Home setPage={setPage} />;
      case "about": return <About />;
      case "services": return <Services />;
      case "contact": return <Contact />;
      case "order-title": return <OrderTitle />;
      case "rates": return <RatesPage />;
      case "links-title-industry": return <TitleIndustryLinksPage />;
      case "links-county-clerks": return <CountyClerksPage />;
      case "form-filler": return <FormFiller />;
      case "pdf-mapper": return <PDFFieldMapper />;
      case "forms-general":
      case "forms-multisite":
      case "forms-miscellaneous":
      case "forms-mortgages-uccs":
      case "forms-transfer-tax":
      case "forms-retainer-deeds":
      case "forms-contracts-of-sale":
        return <FormsPage category={page} />;
      default: return <Home setPage={setPage} />;
    }
  };

  return (
    <div className="ntusa-root">
      <style>{tokens}</style>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html { font-size: 16px; -webkit-font-smoothing: antialiased; scroll-behavior: smooth; }
        body { margin: 0; font-family: var(--font-sans); color: var(--ink); background: var(--white); line-height: 1.6; }
        img { max-width: 100%; height: auto; display: block; }

        /* Navigation */
        .nav-brand:hover { opacity: 0.9; }
        .nav-link { font-size: 0.875rem; font-weight: 500; color: var(--muted); text-decoration: none; position: relative; padding: 0.5rem 0; transition: color 0.2s ease; }
        .nav-link.active { color: var(--navy); font-weight: 600; }
        .nav-link::after { content: ""; position: absolute; bottom: -2px; left: 0; width: 100%; height: 2px; background: var(--gold); transform: scaleX(0); transform-origin: right; transition: transform 0.3s var(--ease-out-expo); }
        .nav-link:hover { color: var(--navy); }
        .nav-link:hover::after, .nav-link.active::after { transform: scaleX(1); transform-origin: left; }

        /* Dropdown menus */
        .dropdown-item:hover { background: var(--surface-legal) !important; color: var(--navy) !important; }

        /* Hero Styling */
        @keyframes heroUnderline { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .hero-corner { position: absolute; width: clamp(50px, 6vw, 80px); height: clamp(50px, 6vw, 80px); z-index: 10; pointer-events: none; }
        .hero-corner--tl { top: -1px; left: -1px; border-top: 3px solid var(--gold); border-left: 3px solid var(--gold); }
        .hero-corner--br { bottom: -1px; right: -1px; border-bottom: 3px solid var(--gold); border-right: 3px solid var(--gold); }
        .hero-frame:hover .hero-image { transform: scale(1) !important; }

        /* Buttons */
        .btn { display: inline-flex; align-items: center; justify-content: center; gap: 0.625rem; padding: 0.9375rem 1.875rem; font-size: 0.9375rem; font-weight: 600; letter-spacing: 0.01em; border: 2px solid transparent; text-decoration: none; cursor: pointer; transition: all 0.3s var(--ease-out-expo); font-family: var(--font-sans); }
        .btn--primary { background: var(--navy); color: var(--white); border-color: var(--navy); }
        .btn--primary:hover { background: var(--navy-light); border-color: var(--navy-light); transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .btn--gold { background: linear-gradient(135deg, var(--gold), var(--gold-rich)); color: var(--navy-deep); border-color: var(--gold); font-weight: 700; }
        .btn--gold:hover { background: linear-gradient(135deg, var(--gold-light), var(--gold)); transform: translateY(-2px); box-shadow: var(--shadow-gold); }
        .btn--outline-light { background: transparent; color: var(--white); border-color: rgba(255,255,255,0.3); }
        .btn--outline-light:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.5); }
        .btn--sm { padding: 0.625rem 1.25rem; font-size: 0.8125rem; }
        .btn svg { transition: transform 0.3s ease; }
        .btn:hover svg { transform: translateX(4px); }

        /* Service Cards */
        .service-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); border-color: var(--border-strong); }
        .service-card:hover .service-accent { transform: scaleY(1) !important; }

        /* Form Items */
        .form-item { transition: all 0.2s ease; }
        .form-item:hover { border-color: var(--gold) !important; background: var(--surface-legal) !important; transform: translateX(4px); }

        /* Form Inputs */
        .form-input { transition: all 0.2s ease; outline: none; }
        .form-input:focus { border-color: var(--gold) !important; background: var(--white) !important; box-shadow: 0 0 0 3px var(--gold-muted); }

        /* Resource Links */
        .resource-link:hover { border-color: var(--gold) !important; color: var(--gold-rich) !important; }

        /* Footer Links */
        .footer-link:hover { color: var(--gold-light) !important; }

        /* Top Bar responsive */
        .top-bar { display: block; }
        @media (max-width: 768px) {
          .top-bar { display: none; }
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: block; }
        }
        .mobile-toggle { display: none; }

        /* Decorative noise overlay (subtle) */
        .ntusa-root::before { content: ""; position: fixed; inset: 0; pointer-events: none; z-index: 9999; opacity: 0.015; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E"); }
      `}</style>
      <MinimalNav currentPage={page} setPage={setPage} />
      <main>{renderPage()}</main>
      <Footer setPage={setPage} />
    </div>
  );
}
